import { useRef, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, CreateMerchantForm, Button, Input, Alert } from '../components'
import { TenantResponse, useTenantService, BRAND_FONT_OPTIONS } from '../services/tenantService'

interface SettingsPageProps {
  tenantDetails: TenantResponse
  onMerchantCreated: (details: TenantResponse) => void
}

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ tenantDetails, onMerchantCreated }) => {
  const { tenant, isMerchantConfigured, hasMerchantId } = tenantDetails
  const { updateTenantProfile, uploadLogo } = useTenantService()

  const [nameDraft, setNameDraft] = useState(tenant.name || '')
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameSaved, setNameSaved] = useState(false)

  const handleSaveName = async () => {
    setNameError(null)
    setNameSaved(false)
    if (!nameDraft.trim()) {
      setNameError('Enter a name')
      return
    }
    setSavingName(true)
    try {
      const updated = await updateTenantProfile({ name: nameDraft.trim() })
      onMerchantCreated(updated)
      setNameSaved(true)
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to save name')
    } finally {
      setSavingName(false)
    }
  }

  const [logoUrlDraft, setLogoUrlDraft] = useState(tenant.logoUrl || '')
  const [brandColorDraft, setBrandColorDraft] = useState(tenant.brandColor || '#0F2742')
  const [fontFamilyDraft, setFontFamilyDraft] = useState(tenant.fontFamily || '')
  const [savingBranding, setSavingBranding] = useState(false)
  const [brandingError, setBrandingError] = useState<string | null>(null)
  const [brandingSaved, setBrandingSaved] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoFileInputRef = useRef<HTMLInputElement>(null)

  const handleSaveBranding = async () => {
    setBrandingError(null)
    setBrandingSaved(false)
    setSavingBranding(true)
    try {
      const updated = await updateTenantProfile({
        logoUrl: logoUrlDraft.trim(),
        brandColor: brandColorDraft.trim(),
        fontFamily: fontFamilyDraft,
      })
      onMerchantCreated(updated)
      setBrandingSaved(true)
    } catch (err) {
      setBrandingError(err instanceof Error ? err.message : 'Failed to save branding')
    } finally {
      setSavingBranding(false)
    }
  }

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setBrandingError(null)
    setBrandingSaved(false)
    setUploadingLogo(true)
    try {
      const updated = await uploadLogo(file)
      onMerchantCreated(updated)
      // Uploading supersedes a pasted URL server-side (see index.ts) — keep
      // the draft in sync so the preview below reflects that immediately.
      setLogoUrlDraft('')
      setBrandingSaved(true)
    } catch (err) {
      setBrandingError(err instanceof Error ? err.message : 'Failed to upload logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  // Free-text vocabulary override applied across the dashboard (see
  // TerminologyContext.tsx) — deliberately never shown to the buyer/seller
  // portal. Blank fields fall back to the standard English word.
  const [clientSingularDraft, setClientSingularDraft] = useState(tenant.terminology?.clientSingular || '')
  const [clientPluralDraft, setClientPluralDraft] = useState(tenant.terminology?.clientPlural || '')
  const [matterSingularDraft, setMatterSingularDraft] = useState(tenant.terminology?.matterSingular || '')
  const [matterPluralDraft, setMatterPluralDraft] = useState(tenant.terminology?.matterPlural || '')
  const [taskSingularDraft, setTaskSingularDraft] = useState(tenant.terminology?.taskSingular || '')
  const [taskPluralDraft, setTaskPluralDraft] = useState(tenant.terminology?.taskPlural || '')
  const [savingTerminology, setSavingTerminology] = useState(false)
  const [terminologyError, setTerminologyError] = useState<string | null>(null)
  const [terminologySaved, setTerminologySaved] = useState(false)

  const handleSaveTerminology = async () => {
    setTerminologyError(null)
    setTerminologySaved(false)
    setSavingTerminology(true)
    try {
      // Always sends the whole map (see PATCH /tenant-details in index.ts) —
      // a blank field intentionally clears back to the default word.
      const updated = await updateTenantProfile({
        terminology: {
          clientSingular: clientSingularDraft.trim(),
          clientPlural: clientPluralDraft.trim(),
          matterSingular: matterSingularDraft.trim(),
          matterPlural: matterPluralDraft.trim(),
          taskSingular: taskSingularDraft.trim(),
          taskPlural: taskPluralDraft.trim(),
        },
      })
      onMerchantCreated(updated)
      setTerminologySaved(true)
    } catch (err) {
      setTerminologyError(err instanceof Error ? err.message : 'Failed to save terminology')
    } finally {
      setSavingTerminology(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div
          className="px-6 sm:px-8 py-6 flex items-start justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, #0F2742, #1F3F66)' }}
        >
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-white truncate">{tenant.companyName || tenant.email}</h2>
            <p className="text-sm text-white/60 mt-1 truncate">{tenant.email}</p>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white">
            <span className={`w-1.5 h-1.5 rounded-full ${isMerchantConfigured ? 'bg-accent' : 'bg-amber-400'}`} />
            {isMerchantConfigured ? 'Fully configured' : 'Setup required'}
          </span>
        </div>

        <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Your Name</h3>
            <p className="text-xs text-gray-500 mb-2">Shown to clients as "Requested by" when you send something for signature.</p>
            {nameError && <Alert type="error" className="mb-2">{nameError}</Alert>}
            <div className="flex items-center gap-2">
              <Input
                value={nameDraft}
                onChange={(e) => { setNameDraft(e.target.value); setNameSaved(false) }}
                placeholder="e.g. Jane Smith"
                className="flex-1"
              />
              <Button type="button" size="sm" variant="secondary" onClick={handleSaveName} disabled={savingName}>
                {savingName ? 'Saving...' : nameSaved ? 'Saved!' : 'Save'}
              </Button>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Company Name</h3>
            <p className="text-gray-600">{tenant.companyName || 'Not set'}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Email</h3>
            <p className="text-gray-600">{tenant.email}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Member Since</h3>
            <p className="text-gray-600">{formatDate(tenant.createdAt) || 'Not set'}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Pinch Merchant ID</h3>
            <p className="text-gray-600">{hasMerchantId ? tenant.merchantId : 'Not configured'}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Signing Page Branding</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-gray-500 mb-4">
            Shown to clients on the document-signing page instead of Conveyo.One's own look. Leave any field blank to keep the default.
          </p>
          {brandingError && <Alert type="error" className="mb-3">{brandingError}</Alert>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUploadLogo}
              />
              <div className="flex items-center gap-2 mb-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => logoFileInputRef.current?.click()} disabled={uploadingLogo}>
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <span className="text-xs text-gray-400">or paste a URL below</span>
              </div>
              <Input
                value={logoUrlDraft}
                onChange={(e) => { setLogoUrlDraft(e.target.value); setBrandingSaved(false) }}
                placeholder="https://example.com/logo.png"
              />
              {(tenantDetails.logoDownloadUrl || logoUrlDraft.trim()) && (
                <img
                  src={tenantDetails.logoDownloadUrl || logoUrlDraft.trim()}
                  alt="Logo preview"
                  className="h-10 mt-2 object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                  onLoad={(e) => { e.currentTarget.style.display = 'block' }}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(brandColorDraft) ? brandColorDraft : '#0F2742'}
                  onChange={(e) => { setBrandColorDraft(e.target.value); setBrandingSaved(false) }}
                  className="w-10 h-10 rounded border border-gray-300 shrink-0 cursor-pointer"
                />
                <Input
                  value={brandColorDraft}
                  onChange={(e) => { setBrandColorDraft(e.target.value); setBrandingSaved(false) }}
                  placeholder="#0F2742"
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font</label>
              <select
                value={fontFamilyDraft}
                onChange={(e) => { setFontFamilyDraft(e.target.value); setBrandingSaved(false) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                style={fontFamilyDraft ? { fontFamily: fontFamilyDraft } : undefined}
              >
                <option value="">Default (Inter)</option>
                {BRAND_FONT_OPTIONS.map((font) => (
                  <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
                ))}
              </select>
            </div>
          </div>

          <Button type="button" size="sm" variant="secondary" className="mt-4" onClick={handleSaveBranding} disabled={savingBranding}>
            {savingBranding ? 'Saving...' : brandingSaved ? 'Saved!' : 'Save Branding'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Terminology</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-gray-500 mb-4">
            Rename these across your dashboard — e.g. call a Client a "Contact". Leave a field blank to keep the
            default. The buyer/seller portal your clients use is unaffected.
          </p>
          {terminologyError && <Alert type="error" className="mb-3">{terminologyError}</Alert>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Client</h4>
              <div className="space-y-2">
                <Input
                  label="Singular"
                  value={clientSingularDraft}
                  onChange={(e) => { setClientSingularDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Client"
                />
                <Input
                  label="Plural"
                  value={clientPluralDraft}
                  onChange={(e) => { setClientPluralDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Clients"
                />
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Matter</h4>
              <div className="space-y-2">
                <Input
                  label="Singular"
                  value={matterSingularDraft}
                  onChange={(e) => { setMatterSingularDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Matter"
                />
                <Input
                  label="Plural"
                  value={matterPluralDraft}
                  onChange={(e) => { setMatterPluralDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Matters"
                />
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Task</h4>
              <div className="space-y-2">
                <Input
                  label="Singular"
                  value={taskSingularDraft}
                  onChange={(e) => { setTaskSingularDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Task"
                />
                <Input
                  label="Plural"
                  value={taskPluralDraft}
                  onChange={(e) => { setTaskPluralDraft(e.target.value); setTerminologySaved(false) }}
                  placeholder="Tasks"
                />
              </div>
            </div>
          </div>

          <Button type="button" size="sm" variant="secondary" className="mt-4" onClick={handleSaveTerminology} disabled={savingTerminology}>
            {savingTerminology ? 'Saving...' : terminologySaved ? 'Saved!' : 'Save Terminology'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-600">Current plan</p>
              <p className="text-lg font-semibold text-gray-900 capitalize mt-0.5">
                {tenant.planKey || 'Not subscribed'}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => window.alert('Manage Subscription — coming soon')}
            >
              Manage Subscription
            </Button>
          </div>
        </CardContent>
      </Card>

      {!isMerchantConfigured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Set Up Conveyo.One Account</CardTitle>
          </CardHeader>
          <CardContent>
            <CreateMerchantForm onCreated={onMerchantCreated} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
